import { Button } from "@/components/ui/button";
import { MOBILE_NAVIGATION_ITEMS } from "@/constants/navigation";
import { Action } from "@/context/AnalyticsProvider";
import { useNavigationAnalytics } from "@/context/AnalyticsProvider/navigation";
import { usePassportProvider } from "@/context/PassportProvider";
import { useActiveRoute } from "@/hooks/useActiveRoute";
import type { NavigationItem } from "@/types/navigation";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";

interface MobileMenuItemProps {
  item: NavigationItem;
  onItemClick: (e: React.MouseEvent, item: NavigationItem) => void;
}

function MobileMenuItem({ item, onItemClick }: MobileMenuItemProps) {
  const active = useActiveRoute(item.url);
  const Icon = item.icon;

  return (
    <Button
      key={item.title}
      variant="ghost"
      size="sm"
      className="flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-all duration-300 ease-out hover:bg-white/10 h-auto"
      onClick={(e) => onItemClick(e, item)}
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon
          className={`w-5 h-5 transition-transform duration-300 ${active ? "text-purple-300" : "text-white/80"}`}
          style={{
            filter: active
              ? "drop-shadow(0 0 8px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 16px rgba(139, 92, 246, 1)) drop-shadow(0 0 24px rgba(139, 92, 246, 0.6))"
              : "drop-shadow(0 0 6px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 12px rgba(103, 126, 234, 0.6))",
          }}
        />
      </div>
      <span
        className={`text-xs transition-all duration-300 ${active ? "text-white font-semibold" : "text-white/70"}`}
      >
        {item.title}
      </span>
    </Button>
  );
}

export function BottomNav() {
  const { track: trackNavigation } = useNavigationAnalytics();
  const router = useRouter();
  const pathname = usePathname();
  const suppressBottomNav = pathname?.includes("/games/fates-exp");
  const { isLoggedIn } = usePassportProvider();
  const handleItemClick = useCallback(
    (e: React.MouseEvent, item: NavigationItem) => {
      if (item.action) {
        e.preventDefault();
        item.action();
      } else {
        router.push(item.url);
      }
      trackNavigation({
        action: Action.PRESSED,
        extras: {
          label: item.title,
          section: "BottomNav",
          url: item.url,
        },
      });
    },
    [router, trackNavigation],
  );

  const navItems = useMemo(() => {
    if (isLoggedIn) {
      return [...MOBILE_NAVIGATION_ITEMS];
    }
    return MOBILE_NAVIGATION_ITEMS;
  }, [isLoggedIn]);

  return (
    <>
      {!suppressBottomNav && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-black/50 backdrop-blur-md border-t border-white/20 shadow-xl md:hidden">
          <div className="flex items-center justify-around px-2 py-3">
            {navItems.map((item) => (
              <MobileMenuItem
                key={item.title}
                item={item}
                onItemClick={handleItemClick}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}
